import {
  put,
  take,
  takeLatest,
  takeEvery,
  select,
  all,
} from 'redux-saga/effects';
import _ from 'lodash';
import {
  TeammoApi,
  normalize,
  compilePaginationObject,
  getStoredUser,
} from 'app/utils/redux/helpers';
import history from 'app/routes/history';
import localStorage from 'local-storage';
import { getUserId } from 'app/ducks/authentication/selectors';
import { teamSchema, teamListSchema } from 'app/utils/redux/schemas';
import { handleError } from 'app/utils/handlers/handleError';
import { loadingStart, loadingFinish } from 'app/ducks/loading/actions';
import {
  sagaActions,
  selectTeam,
  selectLastTeam,
  getAllTeams,
} from 'app/ducks/teams/actions';
import { TeamsActionTypes as types } from 'app/ducks/teams/types';

import { getAllTeamMembers } from 'app/ducks/teamMembers/actions';
import { getOrganisation } from 'app/ducks/organisations/actions';
import { getAllGroups, getGroup } from 'app/ducks/groups/actions';
import { getAllOrganisationSections } from 'app/ducks/organisationSections/actions';
import { getAllPayments } from 'app/ducks/payments/actions';

import { IStoredUser } from 'app/types/common';
import IApplicationState from '../../types/state';

function* createTeamSaga(action): any {
  yield put(sagaActions.createTeamPending(action.payload));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).teams.create(action.payload);

  if (response) {
    const normalizedResponse = normalize(response, teamSchema);

    yield put(sagaActions.createTeamSuccess(normalizedResponse));
    if (!_.isEmpty(response.data.club)) {
      history.push(`/teams/detail/${response.data.id}`);
    } else {
      history.push('/dashboard');
    }
  } else {
    yield put(
      handleError(
        error,
        sagaActions.createTeamFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* updateTeamSaga(action): any {
  yield put(sagaActions.updateTeamPending(action.payload.data));
  const [error, response] = yield (yield TeammoApi(
    yield select(),
  )).teams.update(action.payload.data);

  if (response) {
    const normalizedResponse = normalize(response, teamSchema);
    yield put(sagaActions.updateTeamSuccess(normalizedResponse));
    if (action.payload.callbackFunction !== undefined) {
      action.payload.callbackFunction();
    }
  } else {
    yield put(
      handleError(
        error,
        sagaActions.updateTeamFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  }
}

function* deleteTeamSaga(action): any {
  yield put(sagaActions.deleteTeamPending(action.payload));
  const [error] = yield (yield TeammoApi(yield select())).teams.delete(
    action.payload,
  );

  if (error) {
    yield put(
      handleError(
        error,
        sagaActions.deleteTeamFailed(
          _.isEmpty(error) ? {} : error.response.data.errors,
        ),
      ),
    );
  } else {
    yield put(sagaActions.deleteTeamSuccess(yield getUserId()));
    yield take(types.DELETE_TEAM_SUCCESS);
    yield put(yield selectLastTeam());
  }
}

function* getTeamSaga(action): any {
  yield put(sagaActions.getTeamPending());
  const [error, response] = yield (yield TeammoApi(yield select())).teams.show(
    action.payload,
  );

  if (response) {
    const normalizedResponse = normalize(response, teamSchema);
    yield put(sagaActions.getTeamSuccess(normalizedResponse));
    return normalizedResponse.entities.teams[normalizedResponse.result];
  }
  history.push('/teams');
  yield put(
    handleError(
      error,
      sagaActions.getTeamFailed(
        _.isEmpty(error) ? {} : error.response.data.errors,
      ),
    ),
  );
  return null;
}

function* getAllTeamsSaga(action): any {
  yield put(
    sagaActions.getAllTeamsPending(
      action.payload.compile ? action.payload.pagination : {},
    ),
  );
  yield put(loadingStart());

  const paginationObject = action.payload.compile
    ? compilePaginationObject(yield select((state) => state.teams.pagination))
    : action.payload.pagination;

  const [error, response] = yield (yield TeammoApi(yield select())).teams.list(
    paginationObject,
  );

  if (response) {
    const normalizedResponse = normalize(response, teamListSchema);

    yield put(
      sagaActions.getAllTeamsSuccess(normalizedResponse, response.meta),
    );
    yield put(loadingFinish());
    return normalizedResponse;
  }
  yield put(
    handleError(
      error,
      sagaActions.getAllTeamsFailed(
        _.isEmpty(error) ? {} : error.response.data.errors,
      ),
    ),
  );
}

function* selectLastTeamSaga(): any {
  yield put(sagaActions.selectLastTeamPending());
  const storedUser: IStoredUser = yield getStoredUser();
  // let currentTeamId: string = getCurrentTeamId(yield select(), storedUser);
  const storedTeamId = storedUser.currentTeamId;
  const stateTeamId = yield select(
    (state: IApplicationState) => state.teams.currentTeamId,
  );
  let currentTeamId = storedTeamId || stateTeamId;
  if (_.isEmpty(storedTeamId) && _.isEmpty(stateTeamId)) {
    yield put(yield getAllTeams());
    yield take(types.GET_ALL_TEAMS_SUCCESS);

    const teams = yield select((state) => state.teams);
    if (teams.hasLoaded && teams.teamIds.length > 0) {
      currentTeamId = _.head(teams.teamIds);
    }
  }
  if (_.isEmpty(currentTeamId)) {
    history.push('/create-team');
    yield put(sagaActions.selectLastTeamFinished(currentTeamId));
    return;
  }
  const team = yield* getTeamSaga({ payload: currentTeamId });
  if (_.isEmpty(team)) {
    localStorage.set(yield getUserId(), {});
    yield* selectLastTeamSaga();
    return;
  }
  if (team.organisationId != null) {
    //       put(getAllGroups(team.organisationId)),
    //       put(yield getAllOrganisationSections(team.organisationId)),

    yield all([put(yield getOrganisation(team.organisationId))]);
  }

  // if (team.group !== undefined && team.group.id !== null) {
  //   yield put(yield getGroup(team.group.id));
  // }
  // put(yield getAllTeamMembers(team.id)),
  //   put(yield getAllPayments()),
  // yield all([put(selectTeam(team.id))]);

  const currentTeam = yield select(
    (state) => state.teams.teamsById[state.teams.currentTeamId],
  );
  if (currentTeamId !== undefined && _.isEmpty(currentTeam)) {
    yield take(types.GET_TEAM_SUCCESS);
  }
  yield put(sagaActions.selectLastTeamFinished(currentTeamId));
}

function* selectTeamSaga(action): any {
  const userId = yield getUserId();
  const teamId = action.payload.currentTeamId;
  const team = yield* getTeamSaga({ payload: action.payload.currentTeamId });

  if (team.group !== undefined && team.group.id !== null) {
    yield put(yield getGroup(team.group.id));
  }

  localStorage.set(userId, {
    currentTeamId: teamId,
  });
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createTeamSagaWatcher(): any {
  yield takeLatest(types.CREATE_TEAM_REQUESTED, createTeamSaga);
}

function* updateTeamSagaWatcher(): any {
  yield takeLatest(types.UPDATE_TEAM_REQUESTED, updateTeamSaga);
}

function* deleteTeamSagaWatcher(): any {
  yield takeLatest(types.DELETE_TEAM_REQUESTED, deleteTeamSaga);
}

function* getTeamSagaWatcher(): any {
  yield takeLatest(types.GET_TEAM_REQUESTED, getTeamSaga);
}

function* getAllTeamsSagaWatcher(): any {
  yield takeEvery(types.GET_ALL_TEAMS_REQUESTED, getAllTeamsSaga);
}

function* selectLastTeamSagaWatcher(): any {
  yield takeLatest(types.SELECT_LAST_TEAM_REQUESTED, selectLastTeamSaga);
}

function* selectTeamSagaWatcher(): any {
  yield takeLatest(types.SELECT_TEAM_REQUESTED, selectTeamSaga);
}

export default function* root(): any {
  yield all([
    createTeamSagaWatcher(),
    updateTeamSagaWatcher(),
    deleteTeamSagaWatcher(),
    getTeamSagaWatcher(),
    getAllTeamsSagaWatcher(),
    selectLastTeamSagaWatcher(),
    selectTeamSagaWatcher(),
  ]);
}
